<template>
	<div>
		<v-text-field
			v-bind="$attrs"
			:model-value="selectedLabel"
			readonly
			:disabled="disabled"
			:clearable="Boolean(modelValue)"
			append-inner-icon="icon-chevron-down"
			@click="openSheet"
			@click:clear.stop="clearSelection"
			@click:append-inner="openSheet" />
		<v-bottom-sheet v-model="sheetOpen" inset :content-props="sheetContentProps" :z-index="CONTACT_PICKER_Z_INDEX">
			<v-card class="mobile-customer-picker" data-test-id="mobile-customer-picker" rounded="t-xl" :style="sheetCardStyle">
				<v-card-title class="d-flex align-center justify-space-between">
					<span>Contact</span>
					<v-btn icon="icon-close-medium" variant="text" aria-label="Close contact picker" @click="sheetOpen = false" />
				</v-card-title>
				<v-card-text class="mobile-customer-picker-body">
					<v-text-field
						v-model="searchProxy"
						label="Search contacts"
						variant="outlined"
						density="comfortable"
						clearable
						autofocus
						v-bind="searchFieldProps(searchProxy)" />
					<v-progress-linear v-if="loading" indeterminate color="primary" />
					<v-btn v-if="showCreate" block variant="text" color="primary" class="mb-2" @click="createContact">
						Create contact '{{ search }}'
					</v-btn>
					<v-list class="mobile-customer-picker-list">
						<v-list-item v-for="customer in items" :key="customer.id" @click="select(customer)">
							<v-list-item-title>{{ itemText(customer) }}</v-list-item-title>
							<v-list-item-subtitle v-if="customer.PhoneNumbers?.[0]?.phoneNumber">
								{{ customer.PhoneNumbers[0].phoneNumber }}
							</v-list-item-subtitle>
							<v-list-item-subtitle v-if="customer.EmailAddresses?.[0]?.emailAddress">
								{{ customer.EmailAddresses[0].emailAddress }}
							</v-list-item-subtitle>
						</v-list-item>
						<v-list-item v-if="!loading && items.length === 0">
							<v-list-item-title>No contacts found.</v-list-item-title>
						</v-list-item>
					</v-list>
				</v-card-text>
			</v-card>
		</v-bottom-sheet>
	</div>
</template>

<script setup lang="ts">
	import { searchFieldProps } from "@/helpers/searchLimit";
	import { computed, onBeforeUnmount, onMounted, ref, type CSSProperties } from "vue";
	import { getMobileBottomSheetViewport } from "@/helpers/mobileBottomSheetViewport";
	import type { Customer } from "@/types/Customer";

	defineOptions({ inheritAttrs: false });
	const props = defineProps<{
		items: Customer[];
		loading: boolean;
		search: string;
		modelValue?: Customer | null;
		disabled?: boolean;
		showCreate?: boolean;
		itemText: (customer: Customer) => string;
	}>();
	const emit = defineEmits<{
		(event: "update:search", value: string): void;
		(event: "update:modelValue", value: Customer | null): void;
		(event: "create"): void;
		(event: "open"): void;
	}>();
	const CONTACT_PICKER_Z_INDEX = 3100;
	const sheetOpen = ref(false);
	const keyboardBottomInset = ref(0);
	const visibleViewportHeight = ref<number | null>(null);
	let visualViewportTarget: VisualViewport | null = null;
	const selectedLabel = computed(() => (props.modelValue ? props.itemText(props.modelValue) : ""));
	const searchProxy = computed({ get: () => props.search, set: (value) => emit("update:search", value || "") });
	const sheetContentProps = computed(() => ({
		style: { marginBottom: `${keyboardBottomInset.value}px` }
	}));
	const sheetCardStyle = computed<CSSProperties>(() => {
		const style: CSSProperties = {};
		if (visibleViewportHeight.value) {
			style.maxHeight = `min(80dvh, 42rem, ${Math.max(1, Math.floor(visibleViewportHeight.value) - 16)}px)`;
		}
		return style;
	});

	function updateSheetViewport() {
		const viewport = getMobileBottomSheetViewport(window.innerHeight, window.visualViewport);
		keyboardBottomInset.value = viewport.bottomInset;
		visibleViewportHeight.value = viewport.visibleHeight;
	}

	onMounted(() => {
		updateSheetViewport();
		visualViewportTarget = window.visualViewport ?? null;
		visualViewportTarget?.addEventListener("resize", updateSheetViewport);
		visualViewportTarget?.addEventListener("scroll", updateSheetViewport);
		window.addEventListener("resize", updateSheetViewport);
	});

	onBeforeUnmount(() => {
		visualViewportTarget?.removeEventListener("resize", updateSheetViewport);
		visualViewportTarget?.removeEventListener("scroll", updateSheetViewport);
		visualViewportTarget = null;
		window.removeEventListener("resize", updateSheetViewport);
	});

	function openSheet() {
		if (props.disabled) return;
		sheetOpen.value = true;
		emit("open");
	}

	function select(customer: Customer) {
		emit("update:modelValue", customer);
		sheetOpen.value = false;
	}

	function createContact() {
		// Close this sheet before the parent opens the create-contact UI so two mobile overlays do not
		// remain stacked. Otherwise closing the create form reveals this stale picker underneath it.
		sheetOpen.value = false;
		emit("create");
	}

	function clearSelection() {
		emit("update:modelValue", null);
	}
</script>

<style scoped>
	.mobile-customer-picker {
		display: flex;
		flex-direction: column;
		max-height: min(80dvh, 42rem);
		overflow: hidden;
	}

	.mobile-customer-picker-body {
		min-height: 0;
		overflow-y: auto;
		overscroll-behavior: contain;
		padding-bottom: max(1rem, env(safe-area-inset-bottom));
	}
</style>
